import { css } from '@emotion/react';
import { border_radius_styles_with_size, semantic_colors, shapes } from '../../foundation';
import { buildCSSWithLength } from '../../styles';
import {
  getInputFontStyle,
  getInputHeightBySize,
  getInputHorizontalPaddingBySize,
  input_status_color_css,
} from '../input/styles';
import { InputGroupDividerVariant, InputGroupSize, InputGroupStatus } from './types';

const INPUT_GROUP_SPACING = 8;
const INPUT_GROUP_TIGHT_SPACING = 4;
const INPUT_GROUP_DIVIDER_LENGTH = 16;

/** 구분선을 사이에 둔 두 칸이 각각 선 쪽에 두는 여백. */
export const getInputGroupSpacingBySize = (_size: InputGroupSize) => INPUT_GROUP_SPACING;

/** 구분선이 없는 두 칸이 각각 경계 쪽에 두는 여백. */
export const getInputGroupTightSpacingBySize = (_size: InputGroupSize) => INPUT_GROUP_TIGHT_SPACING;

export const getInputGroupDividerLengthBySize = (_size: InputGroupSize) => INPUT_GROUP_DIVIDER_LENGTH;

interface InputGroupInnerSpacingOptions {
  size: InputGroupSize;
  dividerVariant: InputGroupDividerVariant;
}

export const getInputGroupInnerSpacing = ({ size, dividerVariant }: InputGroupInnerSpacingOptions) => {
  if (dividerVariant === 'full') return getInputHorizontalPaddingBySize(size);
  if (dividerVariant === 'none') return getInputGroupTightSpacingBySize(size);
  return getInputGroupSpacingBySize(size);
};

export const input_group_base_css = css`
  position: relative;
  display: inline-flex;
  box-sizing: border-box;
  align-items: stretch;
  max-width: 100%;
  padding: 0;
  transition: border-color 0.1s linear;
  ${shapes.border_radius.small};
`;

export const getInputGroupSizeStyle = (size: InputGroupSize) => css`
  ${buildCSSWithLength('height', getInputHeightBySize(size))}
  ${getInputFontStyle(size)}
`;

export const input_group_default_style = {
  'backgroundColor': semantic_colors.background.surface,
  'border': `1px solid ${semantic_colors.border.secondary}`,
  'color': semantic_colors.content.primary,
  '&:hover, &:focus-within': {
    borderColor: semantic_colors.border.active,
  },
};

export const input_group_highlighted_style = {
  'backgroundColor': semantic_colors.accent.light,
  'border': `1px solid ${semantic_colors.accent.primary}`,
  'color': semantic_colors.accent.primary,
  '&:hover, &:focus-within': {
    borderColor: semantic_colors.accent.primary,
  },
};

export const input_group_error_style = {
  'border': `1px solid ${input_status_color_css.error}`,
  '&:hover, &:focus-within': {
    borderColor: input_status_color_css.error,
  },
};

export const input_group_disabled_style = {
  'backgroundColor': semantic_colors.background.disabled,
  'border': `1px solid ${semantic_colors.border.disabled}`,
  'color': semantic_colors.content.disabled,
  'cursor': 'not-allowed',
  '&:hover, &:focus-within': {
    borderColor: semantic_colors.border.disabled,
  },
};

export const input_group_highlighted_content_css = css`
  && input {
    color: ${semantic_colors.accent.primary};
    -webkit-text-fill-color: ${semantic_colors.accent.primary};
  }

  && input::placeholder {
    color: ${semantic_colors.accent.primary};
  }
`;

export interface InputGroupSegmentStyleOptions {
  /** 이 칸의 왼쪽 여백. 왼쪽 경계를 소유한 그룹이 정해준 값 */
  startSpacing: number;
  /** 이 칸의 오른쪽 여백. 오른쪽 경계를 소유한 그룹이 정해준 값 */
  endSpacing: number;
  /** 이 칸이 필드(가장 바깥 그룹)의 왼쪽 끝에 닿아 있는지 */
  atFieldStart: boolean;
  /** 이 칸이 필드의 오른쪽 끝에 닿아 있는지 */
  atFieldEnd: boolean;
  /**
   * 이 칸이 자기 `width`를 지정했는지, 지정하지 않았다면 '100%' 디폴트 값에서 auto로 되돌림
   */
  hasOwnWidth: boolean;
}

/**
 * 테두리·배경·라운드를 제거하고 필드 안쪽을 빈틈없이 덮게 합니다.
 *
 * `&&`로 특이도를 올려 Input·DropdownLabel이 자기 css에 적어 둔 테두리와
 * hover/focus 분기를 덮도록 합니다.
 */
export const getInputGroupSegmentStyle = ({
  startSpacing,
  endSpacing,
  atFieldStart,
  atFieldEnd,
  hasOwnWidth,
}: InputGroupSegmentStyleOptions) => {
  const radius = border_radius_styles_with_size.small;
  const startRadius = atFieldStart ? radius : 0;
  const endRadius = atFieldEnd ? radius : 0;

  return css`
    && {
      position: relative;
      height: auto;
      border: none;
      background: none;
      box-shadow: none;
      border-radius: ${startRadius}px ${endRadius}px ${endRadius}px ${startRadius}px;
      ${!hasOwnWidth && 'width: auto;'}
      white-space: nowrap;
      padding-left: ${startSpacing}px;
      padding-right: ${endSpacing}px;
    }

    &&:hover,
    &&:focus,
    &&:focus-within {
      border: none;
      background: none;
    }

    &&:focus-within {
      z-index: 1;
    }
  `;
};

/**
 * 중첩 그룹은 여백과 라운드를 자기 자식에게 넘겨야 하므로 부모 스타일을 리셋합니다.
 */
export const input_group_nested_css = css`
  &&& {
    padding-left: 0;
    padding-right: 0;
    border-radius: 0;
  }
`;

interface InputGroupDividerColorOptions {
  status?: InputGroupStatus;
  disabled?: boolean;
  dividerVariant: InputGroupDividerVariant;
}

export const getInputGroupDividerColor = ({ status, disabled, dividerVariant }: InputGroupDividerColorOptions) => {
  if (disabled) return semantic_colors.border.disabled;

  if (dividerVariant === 'full') {
    if (status === 'error') return input_status_color_css.error;
    if (status === 'highlighted') return semantic_colors.accent.primary;
    return semantic_colors.border.secondary;
  }

  if (status === 'highlighted') return semantic_colors.accent.disabled;
  return semantic_colors.border.primary;
};
